import { motion } from "framer-motion";
import { Zap, Heart, Brain, Shield, Leaf, Clock } from "lucide-react";

const benefits = [
  {
    icon: Zap,
    title: "Accelerated Recovery",
    desc: "BPC-157 and TB-500 peptides dramatically speed up tissue repair, reducing downtime from injuries and surgeries.",
    span: "md:col-span-8",
    bg: "bg-white",
  },
  {
    icon: Heart,
    title: "Anti-Aging",
    desc: "Turn back the clock with peptides that stimulate collagen production and cellular renewal.",
    span: "md:col-span-4",
    bg: "bg-[#EFECE5]",
  },
  {
    icon: Brain,
    title: "Cognitive Enhancement",
    desc: "Sharpen focus and mental clarity with nootropic peptides that support brain health and neuroplasticity.",
    span: "md:col-span-4",
    bg: "bg-[#EFECE5]",
  },
  {
    icon: Shield,
    title: "Immune Support",
    desc: "Thymosin Alpha-1 and other immune-modulating peptides help your body fight infections and build resilience against illness.",
    span: "md:col-span-8",
    bg: "bg-white",
  },
  {
    icon: Leaf,
    title: "Weight Management",
    desc: "Optimize your metabolism naturally with peptides that target fat loss while preserving lean muscle mass.",
    span: "md:col-span-6",
    bg: "bg-white",
  },
  {
    icon: Clock,
    title: "Better Sleep",
    desc: "Restore deep, restorative sleep cycles with peptides that regulate your circadian rhythm and growth hormone release.",
    span: "md:col-span-6",
    bg: "bg-[#EFECE5]",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function BenefitsSection() {
  return (
    <section id="benefits" data-testid="benefits-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
            Why Peptide Recovery
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#2A3324]">
            Benefits That Transform
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className={`${b.span} ${b.bg} rounded-[2rem] p-8 md:p-10 border border-[#2A3324]/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-2 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500`}
            >
              <b.icon
                size={32}
                strokeWidth={1}
                className="text-[#5C715E] mb-6"
              />
              <h3 className="text-2xl md:text-3xl font-serif font-normal tracking-tight text-[#2A3324] mb-3">
                {b.title}
              </h3>
              <p className="text-base font-sans font-light leading-relaxed text-[#2A3324]/70">
                {b.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
